import React, { useState, useEffect, FC, ReactNode, useCallback } from "react"
import styled from "styled-components"
import { Field } from "redux-form/immutable"
import { TextInput } from "@mantine/core"

import { StyledAlert } from "QuorumGrassroots/styled-components/components/StyledAlert"
import { sendEmailConfirmationCode } from "QuorumGrassroots/services"
import { StyledContrastText } from "QuorumGrassroots/styled-components/components/StyledContrastText"
import { constants } from "QuorumGrassroots/constants"
import { styleWithOrgDesignHelper } from "QuorumGrassroots/styled-components/helperFunctions"
import { EmailField } from "shared-web/forms/fields/EmailField"
import { useDebouncedFunctionWithValue } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/useDebouncedFunctionWithValue"

// Custom styled wrapper for the input
const StyledInputWrapper = styled.div`
    margin-top: 8px;
    margin-bottom: 0;
`

// Custom styled input for verification code using Mantine
const StyledMantineInput = styled(TextInput)`
    width: 100%;

    .mantine-TextInput-input {
        text-align: center;
        letter-spacing: 2px;
        font-size: 16px;
        min-height: 40px;
        border-radius: 4px;
        padding: 8px 12px;
    }
`

// Wrap with organization design helper
const StyledVerificationInput = styleWithOrgDesignHelper(StyledMantineInput)

interface ResendButtonProps {
    disabled?: boolean
    hasError?: boolean
}

// Styled resend button
const ResendButton = styled.button<ResendButtonProps>`
    background: none;
    border: none;
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    font-size: 14px;
    padding: 0;
    margin-top: ${(props) => (props.hasError ? "24px" : "8px")};
    text-align: center;
    width: 100%;
    text-decoration: ${(props) => (props.disabled ? "none" : "underline")};
`

// Styled error message
const ErrorMessage = styled.div`
    color: ${constants.errorTextColor};
    font-size: 12px;
    margin-top: 4px;
    position: absolute;
    width: 100%;
    text-align: center;
`

interface StyledNotificationProps {
    mb?: string
    color?: string
}

// Custom styled alert for notifications
const StyledNotification = styled(StyledAlert)<StyledNotificationProps>`
    position: relative;
    border: 8px;
    margin-bottom: ${(props) => (props.mb === "sm" ? "16px" : "0")};
    padding-right: 30px; // Space for close button
    background-color: ${(props) =>
        props.color === "red"
            ? `${constants.errorTextColor}20` // 20% opacity
            : `${constants.colors.green[0]}`};
    color: ${(props) => (props.color === "red" ? constants.errorTextColor : constants.colors.green[6])};
    gap: 8px;
`

const NotificationCloseButton = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    font-size: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`

interface NotificationProps {
    color?: string
    title?: string
    children: ReactNode
    onClose?: () => void
    mb?: string
}

const Notification: FC<NotificationProps> = ({ color, title, children, onClose, mb }) => {
    return (
        <StyledNotification mb={mb} isCampaignPage activateNGGTheme color={color}>
            <i
                className={`fa fa-${color === "red" ? "exclamation-circle" : "check-circle"}`}
                style={{ marginRight: "8px" }}
            />
            <div>
                {title && <strong>{title}: </strong>}
                {children}
            </div>
            {onClose && (
                <NotificationCloseButton onClick={onClose} aria-label="Close notification">
                    <i className="fa fa-times" />
                </NotificationCloseButton>
            )}
        </StyledNotification>
    )
}

// Validation function for the email confirmation code
const validateRequiredEmailConfirmationCode = (value: string): string | undefined => {
    if (!value) return "Please enter the verification code"
    if (!/^\d{6}$/.test(value)) return "Please enter a valid 6-digit code"
    return undefined
}

const VerificationInputField = ({
    input,
    meta,
    organizationDesign,
    dataCy,
    isCampaignPage,
    onResendClick,
    resendDisabled,
    resendCountdown,
}) => {
    const hasErrorText = (meta.touched || meta.submitFailed) && meta.error

    if (!meta.touched && meta.pristine && meta.submitFailed && !input.value) {
        // If the form was submitted but the field is empty, mark it as touched to show the error
        setTimeout(() => input.onBlur(), 0)
    }

    return (
        <>
            <StyledVerificationInput
                placeholder="Enter code"
                value={input.value}
                onChange={(event) => {
                    const cleanedValue = event.currentTarget.value.replace(/\D/g, "").substring(0, 6)
                    input.onChange(cleanedValue)
                }}
                maxLength={6}
                data-cy={dataCy}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                organizationDesign={organizationDesign}
                styles={{
                    input: {
                        borderColor: hasErrorText ? constants.errorTextColor : constants.inputFieldBorderColor,
                    },
                }}
            />
            {hasErrorText && <ErrorMessage>{meta.error}</ErrorMessage>}
            {onResendClick && (
                <ResendButton onClick={onResendClick} disabled={resendDisabled} type="button" hasError={hasErrorText}>
                    <StyledContrastText isCampaignPage={isCampaignPage}>
                        {resendDisabled ? `Resend email (${resendCountdown}s)` : "Resend email"}
                    </StyledContrastText>
                </ResendButton>
            )}
        </>
    )
}

export { validateRequiredEmailConfirmationCode, VerificationInputField }

interface NotificationType {
    type: "success" | "error"
    message: string
}

const UnconnectedEmailFieldWithConfirmation = (props) => {
    const {
        name,
        dataCy,
        label,
        placeholder,
        displayErrorWithoutTouch,
        validate,
        accessibilityId,
        showAsterisk,
        isCampaignPage,
        campaign,
        disabled,
        tooltipText,
        uniqueWidgetId,
        organizationDesign,
        ...restProps
    } = props

    const [showVerificationInput, setShowVerificationInput] = useState<boolean>(false)
    const [emailValue, setEmailValue] = useState<string>("")
    const [notification, setNotification] = useState<NotificationType | null>(null)
    const [resendDisabled, setResendDisabled] = useState<boolean>(false)
    const [resendCountdown, setResendCountdown] = useState<number>(60)

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null
        if (resendDisabled && resendCountdown > 0) {
            timer = setTimeout(() => {
                setResendCountdown(resendCountdown - 1)
            }, 1000)
        } else if (resendCountdown === 0) {
            setResendDisabled(false)
            setResendCountdown(60)
        }
        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [resendDisabled, resendCountdown])

    const startResendCountdown = () => {
        setResendDisabled(true)
        setResendCountdown(60)
    }

    const handleResendClick = async () => {
        if (resendDisabled || !emailValue) return
        try {
            startResendCountdown()
            await sendEmailConfirmationCode(emailValue)
            setNotification({
                type: "success",
                message: "Confirmation code sent to your email",
            })
        } catch (error) {
            setNotification({
                type: "error",
                message: "Failed to send confirmation code. Please try again.",
            })
        }
    }

    const handleDebouncedEmail = useCallback(async (email: string) => {
        if (!email || !email.includes("@") || !email.includes(".")) return
        try {
            await sendEmailConfirmationCode(email)
            setShowVerificationInput(true)
            setNotification({
                type: "success",
                message: "Confirmation code sent to your email",
            })
        } catch (error: any) {
            if (error.response && error.response.status === 404) {
                setShowVerificationInput(false)
            } else {
                setShowVerificationInput(true)
                setNotification({
                    type: "error",
                    message: "Failed to send confirmation code. Please try again.",
                })
            }
        }
    }, [])

    useDebouncedFunctionWithValue(handleDebouncedEmail, emailValue, 500)

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value
        setEmailValue(email)
    }

    const commonProps = {
        name,
        dataCy,
        label,
        placeholder,
        displayErrorWithoutTouch,
        validate,
        accessibilityId,
        showAsterisk,
        isCampaignPage,
        campaign,
        disabled: disabled,
        tooltipText,
        uniqueWidgetId,
        ...restProps,
        onChange: handleOnChange,
    }

    const EmailComponent = props.component || EmailField

    return (
        <div>
            <EmailComponent {...commonProps} />

            {showVerificationInput && (
                <StyledInputWrapper>
                    {notification && (
                        <Notification
                            color={notification.type === "error" ? "red" : "green"}
                            title={notification.type === "error" ? "Error" : "Success"}
                            onClose={() => setNotification(null)}
                            mb="sm"
                        >
                            {notification.message}
                        </Notification>
                    )}
                    <div style={{ position: "relative" }}>
                        <Field
                            name="confirmation_code"
                            component={VerificationInputField}
                            organizationDesign={organizationDesign}
                            dataCy={`${dataCy}-verification-input`}
                            validate={validateRequiredEmailConfirmationCode}
                            isCampaignPage={isCampaignPage}
                            onResendClick={handleResendClick}
                            resendDisabled={resendDisabled}
                            resendCountdown={resendCountdown}
                        />
                    </div>
                </StyledInputWrapper>
            )}
        </div>
    )
}

export const EmailFieldWithConfirmation = UnconnectedEmailFieldWithConfirmation
